import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import { Button } from 'src/components/system/Button';
import { useAuthProfile } from 'src/hooks/useAuthProfile';
import {
  fetchQuickGameLeaderboard,
  QuickGameQuestion,
  QuickGameScore,
  startQuickGame,
  submitQuickGameResult,
} from 'src/services/quickGame';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 32px 24px;
`;

const Grid = styled.div`
  display: grid;
  gap: 20px;
  grid-template-columns: 1fr;

  @media (min-width: 1024px) {
    grid-template-columns: 2fr 1fr;
  }
`;

const Card = styled.div`
  background: #fff;
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Countdown = styled.div`
  font-size: 48px;
  font-weight: 600;
  text-align: center;
`;

const CountdownImage = styled.img`
  width: min(240px, 70%);
  margin: 0 auto;
`;

const AnswerButton = styled.button<{ selected: boolean; correct: boolean | null }>`
  width: 100%;
  text-align: left;
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid
    ${({ selected, correct }) => {
      if (correct === true) return '#5AB56E';
      if (correct === false && selected) return '#D81E5B';
      return '#e0e0e0';
    }};
  background:
    ${({ selected, correct }) => {
      if (correct === true) return '#F1FAF3';
      if (correct === false && selected) return '#FFF1F6';
      return '#F7F7F7';
    }};
  cursor: pointer;
  display: flex;
  gap: 10px;

  &:hover {
    background: ${({ correct }) => (correct === null ? '#EFEFEF' : undefined)};
  }
`;

const LeaderboardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const LeaderboardRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f7f7f7;
  border-radius: 8px;
  padding: 10px 12px;
`;

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #eef2ff;
  color: #222b82;
  padding: 6px 10px;
  border-radius: 999px;
  font-weight: 500;
`;

const ResultImage = styled.img`
  width: min(260px, 80%);
  align-self: center;
`;

type GameStatus = 'idle' | 'countdown' | 'playing' | 'finished';

type QuestionResult = {
  questionId: number;
  correct: boolean;
  timeSeconds: number;
};

const assets = {
  countdown: '/images/quick-game/3.png',
  celebrate: '/images/quick-game/02.png',
  lowScore: '/images/quick-game/1.png',
  almost: '/images/quick-game/4.png',
};

export function QuickGame() {
  const [status, setStatus] = useState<GameStatus>('idle');
  const [countdown, setCountdown] = useState(3);
  const [questions, setQuestions] = useState<QuickGameQuestion[]>([]);
  const [current, setCurrent] = useState(0);
  const [timeLeft, setTimeLeft] = useState(5);
  const [results, setResults] = useState<QuestionResult[]>([]);
  const [leaderboard, setLeaderboard] = useState<QuickGameScore[]>([]);
  const { profile } = useAuthProfile();
  const [playerName, setPlayerName] = useState('Você');
  const [lastScore, setLastScore] = useState<QuickGameScore | null>(null);
  const [isTopTen, setIsTopTen] = useState(false);

  const questionStartRef = useRef<number | null>(null);
  const [showResultImage, setShowResultImage] = useState(false);
  const registeringRef = useRef(false);

  useEffect(() => {
    document.title = 'Desafio do Toro | ART - Simulatoro';
  }, []);

  useEffect(() => {
    if (profile?.name && (playerName === 'Você' || playerName.trim().length === 0)) {
      setPlayerName(profile.name);
    }
  }, [profile?.name, playerName]);

  useEffect(() => {
    (async () => {
      const scores = await fetchQuickGameLeaderboard();
      setLeaderboard(scores);
    })();
  }, []);

  useEffect(() => {
    if (status !== 'countdown') return;

    if (countdown <= 0) {
      const timer = setTimeout(() => {
        setStatus('playing');
        setTimeLeft(5);
        questionStartRef.current = Date.now();
      }, 700);

      return () => clearTimeout(timer);
    }

    const timer = setTimeout(() => setCountdown(prev => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [status, countdown]);

  const startGame = useCallback(async () => {
    try {
      setStatus('countdown');
      setCountdown(3);
      setResults([]);
      setCurrent(0);
      setTimeLeft(5);
      setShowResultImage(false);
      registeringRef.current = false;
      const session = await startQuickGame();
      setQuestions(session.questions);
      questionStartRef.current = null;
    } catch (error) {
      toast.error('Não foi possível iniciar o Desafio do Toro.');
      setStatus('idle');
    }
  }, []);

  const handleAnswer = useCallback(
    async (answerId: number | null) => {
      if (registeringRef.current) return;
      const question = questions[current];
      if (!question) return;

      registeringRef.current = true;
      const now = Date.now();
      const elapsed = questionStartRef.current
        ? Math.min(5, (now - questionStartRef.current) / 1000)
        : 5;
      const rounded = Number(elapsed.toFixed(2));
      const isCorrect = answerId === question.correct_answer_id;

      const updatedResults = [
        ...results,
        {
          questionId: question.id,
          correct: Boolean(isCorrect),
          timeSeconds: rounded,
        },
      ];

      setResults(updatedResults);

      const hasNext = current + 1 < questions.length;
      if (hasNext) {
        setCurrent(prev => prev + 1);
        setTimeLeft(5);
        questionStartRef.current = Date.now();
        registeringRef.current = false;
      } else {
        setStatus('finished');
        setTimeLeft(0);
        questionStartRef.current = null;

        const summary = summarizeResults(updatedResults);
        const scorePayload: QuickGameScore = {
          player: playerName.trim() || profile?.name || 'Você',
          totalCorrect: summary.totalCorrect,
          totalQuestions: questions.length,
          totalTimeSeconds: Number(summary.totalTimeSeconds.toFixed(2)),
          finishedAt: new Date().toISOString(),
        };
        setLastScore(scorePayload);

        try {
          const updated = await submitQuickGameResult(scorePayload);
          setLeaderboard(updated);
          setIsTopTen(isScoreInList(updated, scorePayload));
        } catch (error) {
          toast.error('Não foi possível registrar o resultado.');
        }

        registeringRef.current = false;
    }
  },
  [current, playerName, profile?.name, questions, results],
);

useEffect(() => {
  if (status !== 'playing') return;
  if (timeLeft <= 0) {
    handleAnswer(null);
    return;
  }

  const timer = setTimeout(() => setTimeLeft(prev => prev - 1), 1000);
  return () => clearTimeout(timer);
}, [status, timeLeft, handleAnswer]);

  const summary = useMemo(() => summarizeResults(results), [results]);
  const resultVisual = useMemo(() => resolveResultVisual(lastScore, isTopTen), [lastScore, isTopTen]);

  useEffect(() => {
    if (status === 'finished' && resultVisual?.src) {
      if (!showResultImage) {
        const timer = setTimeout(() => setShowResultImage(true), 500);
        return () => clearTimeout(timer);
      }
      return;
    }

    if (showResultImage) {
      setShowResultImage(false);
    }
  }, [status, resultVisual, showResultImage]);

  return (
    <Page>
      <h1>Desafio do Toro</h1>
      <p>
        Encare 5 questões relâmpago de famílias diferentes. Você tem até <strong>5 segundos</strong> por
        pergunta. Ganha quem acerta mais no menor tempo total!
      </p>
      <Grid>
        <Card>
          {status === 'idle' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <span>Seu nome no ranking</span>
                  <input
                    value={playerName}
                    onChange={event => setPlayerName(event.target.value)}
                    placeholder="Digite como quer aparecer"
                    style={{
                      padding: '10px 12px',
                      borderRadius: 8,
                      border: '1px solid #d6d6d6',
                    }}
                  />
                </label>
              </div>
              <Button
                width="220px"
                backgroundColor="#222B82"
                borderColor="#222B82"
                buttonColor="#fff"
                onClick={startGame}
                label={<span>Iniciar jogo</span>}
              />
            </div>
          )}

          {status === 'countdown' && (
            <div style={{ textAlign: 'center', padding: '30px 0' }}>
              <p>Prepare-se!</p>
              <Countdown>{countdown}</Countdown>
            </div>
          )}

          {status === 'playing' && questions[current] && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Tag>{questions[current].familyName}</Tag>
                <Tag>{timeLeft}s</Tag>
              </div>
              <div>
                <strong>
                  Questão {current + 1}/{questions.length}
                </strong>
              </div>
              <div>{questions[current].statement}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {questions[current].answers.map(answer => (
                  <AnswerButton
                    key={answer.id}
                    selected={false}
                    correct={null}
                    onClick={() => handleAnswer(answer.id)}
                  >
                    {answer.text}
                  </AnswerButton>
                ))}
              </div>
            </div>
          )}

          {status === 'finished' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <h2>Resultado</h2>
              <p>
                Você acertou {summary.totalCorrect} de {questions.length} questões.
              </p>
              <p>
                Tempo total: <strong>{summary.totalTimeSeconds.toFixed(2)}s</strong>
              </p>
              {showResultImage && resultVisual && resultVisual.src && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                  <ResultImage src={resultVisual.src} alt={resultVisual.alt} />
                  {resultVisual.caption && <strong style={{ textAlign: 'center' }}>{resultVisual.caption}</strong>}
                </div>
              )}
              <Button
                width="220px"
                backgroundColor="#222B82"
                borderColor="#222B82"
                buttonColor="#fff"
                onClick={startGame}
                label={<span>Jogar novamente</span>}
              />
            </div>
          )}
        </Card>

        <Card>
          <h2>Top 10</h2>
          <LeaderboardList>
            {leaderboard.map((item, index) => (
              <LeaderboardRow key={`${item.player}-${index}`}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <strong>
                    {index + 1}. {item.player}
                  </strong>
                  <small>
                    {item.totalCorrect}/{item.totalQuestions} em {item.totalTimeSeconds.toFixed(2)}s
                  </small>
                </div>
                <span style={{ color: '#a4a4a4', fontSize: '0.85rem' }}>
                  {new Date(item.finishedAt).toLocaleString('pt-BR')}
                </span>
              </LeaderboardRow>
            ))}
            {leaderboard.length === 0 && <div>Nenhum resultado registrado ainda.</div>}
          </LeaderboardList>
        </Card>
      </Grid>
    </Page>
  );
}

function summarizeResults(list: QuestionResult[]) {
  if (list.length === 0) {
    return { totalCorrect: 0, totalTimeSeconds: 0 };
  }

  return list.reduce(
    (acc, current) => {
      return {
        totalCorrect: acc.totalCorrect + (current.correct ? 1 : 0),
        totalTimeSeconds: acc.totalTimeSeconds + current.timeSeconds,
      };
    },
    { totalCorrect: 0, totalTimeSeconds: 0 },
  );
}

function isScoreInList(list: QuickGameScore[], target: QuickGameScore) {
  const tolerance = 0.01;
  return list.some(item =>
    item.player === target.player &&
    item.totalCorrect === target.totalCorrect &&
    item.totalQuestions === target.totalQuestions &&
    Math.abs(item.totalTimeSeconds - target.totalTimeSeconds) < tolerance,
  );
}

function resolveResultVisual(score: QuickGameScore | null, topTen: boolean) {
  if (!score) return null;

  if (topTen) {
    if (score.totalCorrect === score.totalQuestions) {
      return {
        src: assets.celebrate,
        alt: 'Mandou bem! Top 10 com gabarito perfeito.',
        caption: 'Imbatível! Você entrou no Top 10 com 100% de acertos.',
      };
    }
    return {
      src: assets.celebrate,
      alt: 'Entrou no Top 10!',
      caption: 'Top 10 garantido! Continue acelerando para buscar a liderança.',
    };
  }

  if (score.totalCorrect === score.totalQuestions) {
    return {
      src: assets.almost,
      alt: 'Por pouco!',
      caption: 'Gabaritou, mas ficou fora do Top 10. Bora tentar de novo mais rápido!',
    };
  }

  if (score.totalCorrect <= 3) {
    return {
      src: assets.lowScore,
      alt: 'Precisa treinar mais',
      caption: 'Força! Revise os conteúdos e volte para melhorar esse placar.',
    };
  }

  return {
    src: assets.almost,
    alt: 'Bom desempenho',
    caption: 'Faltou pouco para entrar no Top 10. Continue praticando!',
  };
}
